<template>
  <form
    class="db-modal-addons-content db-modal-form"
    novalidate
  >
    <div class="db-modal-form-content">
      <DbCard
        v-for="(addon, i) in addons"
        :key="i"
      >
        <template
          v-slot:content
          v-if="addon.unitPrice"
        >
          <DbCardContentAddon
            :isInCart="isInCart.filter((o) => o.id === addon.sourceItemId).length > 0"
            :disabled="addon.preventQtyAdjustment"
            :defaultQuantity="
              isInCart.filter((o) => o.id === addon.sourceItemId)?.[0]?.qty
                ? isInCart.filter((o) => o.id === addon.sourceItemId)[0].qty
                : addon.calculatedQuantity
            "
            v-on:add-to-cart="selectAddon(addon.sourceItemId, $event, addon.currencyCode)"
            v-on:update-cart="updateAddon(addon.sourceItemId, $event, addon.currencyCode)"
            v-on:delete="removeAddon(addon.sourceItemId)"
          >
            <template v-slot:title>
              {{ addon.name }}
            </template>
            <template v-slot:description>
              <div
                class="db-wysiwyg"
                v-if="addon.xRichDescription"
                v-html="addon.xRichDescription"
              />
            </template>
            <template v-slot:rate>
              <!-- TODO: xo7: Handle spinner and price loading -->
              <!-- <DbSpinner /> v-if / v-else -->
              <DbRate>
                <template v-slot:rate>
                  {{ getCurrencySign(String(addon.currencyCode)) }}
                  {{
                    Math.round(Number(0 ? addon.inclusiveUnitPrice : addon.unitPrice) * 100) / 100
                  }}
                </template>
                <template v-slot:suffix>
                  {{ `${getPackageUnit(addon.quantityCalculation)}` }}
                </template>
                <template v-slot:footernote>
                  {{ 0 ? $t('Inclusive of taxes and fees') : $t('Exclusive of taxes and fees') }}
                </template>
              </DbRate>
            </template>
          </DbCardContentAddon>
        </template>
      </DbCard>
    </div>

    <div class="db-modal-form-footer">
      <button
        type="button"
        class="db-button-tertiary db-button-large db-modal-addons-cancel"
        v-on:click="$emit('close')"
      >
        {{ $t('Cancel') }}
      </button>
      <button
        type="button"
        class="db-button-secondary db-button-large db-modal-addons-confirm"
        v-on:click="addToCart"
      >
        {{ $t('Confirm selection') }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { reactive } from 'vue'
import type {
  PackageAddOn,
  CardAddonsItem,
} from '@/pages/booking-funnel/packageConfiguration/types/packageData'
import { getCurrencySign, getPackageUnit } from '@/utils/currency'
import { useCartStore } from '@/stores/cart'
import { storeToRefs } from 'pinia'
import { useGtm } from '@/composables/features/useGtm'
import { usePackagesStore } from '@/stores/packages'
const { event } = useGtm()
const packagesStore = usePackagesStore()

const cartStore = useCartStore()

const defaultCurrencyCode = storeToRefs(cartStore)
const { cartSummary } = storeToRefs(cartStore)
const hotel = window.db2BookingFunnel.getHotel()

// TODO: xo7: Add props to handle addons
const props = defineProps<{
  addons: PackageAddOn[]
  selectedAddons?: CardAddonsItem[]
}>()

const emit = defineEmits<{
  (e: 'submit', isInCart: { id: string; qty: number; currencyCode: string }[]): void
  (e: 'close'): void
}>()

const isInCart = reactive<{ id: string; qty: number; currencyCode: string }[]>([])
// Init isInCart if already addons save in cart
if (props.selectedAddons) {
  props.selectedAddons.map((addon) => {
    isInCart.push({
      id: addon.addonId,
      qty: addon.quantity,
      currencyCode: addon.currencyCode,
    })
  })
}

/**
 * selectAddon - store addons that are selected in the modal
 * @param addonId string
 * @param quantity number
 */
const selectAddon = (addonId: string, quantity: number, currencyCode: string) => {
  isInCart.push({
    id: addonId,
    qty: quantity,
    currencyCode:
      currencyCode !== '' ? currencyCode : defaultCurrencyCode.currencyCode.value ?? 'USD',
  })

  let currentAddon = props.addons.find((item) => addonId === item.sourceItemId)
  let currentAddonIdx = props.addons.findIndex((item) => addonId === item.sourceItemId)
  if (currentAddon && cartSummary.value && packagesStore.attendees) {
    let attendees_bucket = ''
    if (packagesStore.attendees >= 0 && packagesStore.attendees <= 5) attendees_bucket = '0-5'
    if (packagesStore.attendees >= 6 && packagesStore.attendees <= 10) attendees_bucket = '6-10'
    if (packagesStore.attendees >= 11 && packagesStore.attendees <= 20) attendees_bucket = '11-20'
    if (packagesStore.attendees >= 21 && packagesStore.attendees <= 30) attendees_bucket = '21-30'
    if (packagesStore.attendees > 30) attendees_bucket = '+30'
    event('add_to_cart', {
      currency: cartSummary.value.currencyCode,
      items: {
        item_id: addonId,
        item_name: currentAddon.name,
        affiliation: hotel.name,
        index: currentAddonIdx,
        item_brand: hotel.master_item?.name,
        item_category: 'meeting rooms addons',
        item_list_id: 'addons_list',
        item_list_name: 'Addons List',
        item_variant: '',
        attendees_bucket: attendees_bucket,
        quantity: quantity,
      },
    })
  }
}

/**
 * Update quantity in isInCart Array on change quantity input
 */
const updateAddon = (addonId: string, quantity: number, currencyCode: string) => {
  if (isInCart.length > 0) {
    isInCart.map((isIn) => {
      if (String(isIn.id) === String(addonId)) {
        isIn.qty = quantity
        isIn.currencyCode =
          currencyCode !== '' ? currencyCode : defaultCurrencyCode.currencyCode.value ?? 'USD'
      }
    })
  }
}

const removeAddon = (addonId: string) => {
  isInCart.map((addonInCart, index) => {
    let currentAddon = props.addons.find((item) => addonId === item.sourceItemId)
    if (currentAddon && addonInCart.id === addonId) {
      let attendees_bucket = ''
      if (packagesStore.attendees >= 0 && packagesStore.attendees <= 5) attendees_bucket = '0-5'
      if (packagesStore.attendees >= 6 && packagesStore.attendees <= 10) attendees_bucket = '6-10'
      if (packagesStore.attendees >= 11 && packagesStore.attendees <= 20) attendees_bucket = '11-20'
      if (packagesStore.attendees >= 21 && packagesStore.attendees <= 30) attendees_bucket = '21-30'
      if (packagesStore.attendees > 30) attendees_bucket = '+30'
      event('remove_from_cart', {
        currency: cartSummary.value.currencyCode,
        items: {
          item_id: addonId,
          item_name: currentAddon.name,
          affiliation: hotel.name,
          index: 0,
          item_brand: hotel.master_item?.name,
          item_category: 'meeting rooms addons',
          item_list_id: 'addons_list',
          item_list_name: 'Addons List',
          item_variant: '',
          attendees_bucket: attendees_bucket,
          quantity: addonInCart.qty,
        },
      })
      isInCart.splice(index, 1)
    }
  })
}

const addToCart = () => {
  emit('submit', isInCart)
}
</script>
